import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaSyncAlt,
  FaExchangeAlt,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const BANKER_API = `https://c6n2cvb7c9.execute-api.ap-south-1.amazonaws.com/default/bankersTransaction`;
const MAIN_BRANCH_API = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;

export default function BTBBankerReconciliation() {
  const [bankerTxs, setBankerTxs] = useState([]);
  const [branchTxs, setBranchTxs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resB, resM] = await Promise.all([
        fetch(BANKER_API),
        fetch(MAIN_BRANCH_API),
      ]);
      const dataB = await resB.json();
      const dataM = await resM.json();

      // 1. Banker Side: Take everything. The 'Banker' is whoever is in fromBranch/toBranch
      setBankerTxs(dataB.transactions || []);

      // 2. Branch Side: Still filter for BTB's active role
      const onlyBranchBTB = (dataM.transactions || []).filter(
        (tx) =>
          (tx.fromBranch === "BTB" && tx.flowRole === "SENDER") ||
          (tx.toBranch === "BTB" && tx.flowRole === "RECEIVER"),
      );
      setBranchTxs(onlyBranchBTB);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const auditData = useMemo(() => {
    const processTransactions = (txList, side) => {
      const grouped = {};

      // DEBUG LOG 1: See the first transaction to check field names
      if (txList.length > 0) {
        console.warn(`--- DEBUG: RAW DATA FOR ${side} ---`);
        console.log("First Transaction Keys:", Object.keys(txList[0]));
        console.log("First Transaction Data:", txList[0]);
      }

      txList.forEach((tx, idx) => {
        if (["REJECTED", "SPLIT"].includes(tx.status)) return;
        if (tx.status === "PENDING" && tx.flowRole === "RECEIVER") return;
        const bankerBranch =
          tx.fromBranch === "BTB" ? tx.toBranch : tx.fromBranch;
        const partyName = tx.name || "General";
        const groupKey = `${bankerBranch}-${partyName}`;

        // DEBUG LOG 2: Check specifically for opening balance fields
        const foundOpening =
          tx.branchOb || tx.openingBalance || tx.opening || 0;
        if (idx < 2) {
          console.log(
            `Item ${idx} (${side}) Opening Balance Found:`,
            foundOpening,
          );
        }

        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            banker: bankerBranch,
            party: partyName,
            // Try different possible field names based on your DB setup
            opening: Number(foundOpening),
            totIn: 0,
            totOut: 0,
            com: 0,
            exp: 0,
          };
        }

        const amt = Number(tx.amount || 0);
        const comm = Number(tx.commission || 0);

        if (["IN", "BTIN", "SPLITIN"].includes(tx.paymentType)) {
          grouped[groupKey].totIn += amt;
        } else if (["OUT", "BTOUT", "SPLITOUT"].includes(tx.paymentType)) {
          grouped[groupKey].totOut += amt;
        } else if (tx.paymentType === "EXP") {
          grouped[groupKey].exp += amt;
        }

        grouped[groupKey].com += comm;
      });

      return grouped;
    };

    // --- EXECUTION ---
    const bGrouped = processTransactions(bankerTxs, "BANKER SIDE");
    const mGrouped = processTransactions(branchTxs, "BRANCH SIDE");

    const bankersRows = Object.values(bGrouped).map((vals) => ({
      ...vals,
      closing: vals.opening + vals.totIn + vals.com - (vals.totOut + vals.exp),
    }));

    const bankersGrandTotal = bankersRows.reduce(
      (acc, row) => ({
        opening: acc.opening + row.opening,
        totIn: acc.totIn + row.totIn,
        totOut: acc.totOut + row.totOut,
        com: acc.com + row.com,
        exp: acc.exp + row.exp,
        closing: acc.closing + row.closing,
      }),
      { opening: 0, totIn: 0, totOut: 0, com: 0, exp: 0, closing: 0 },
    );

    const btbGrandTotal = Object.values(mGrouped).reduce(
      (acc, vals) => {
        acc.opening += vals.opening;
        acc.totIn += vals.totIn;
        acc.totOut += vals.totOut;
        acc.com += vals.com;
        acc.exp += vals.exp;
        return acc;
      },
      { opening: 0, totIn: 0, totOut: 0, com: 0, exp: 0 },
    );

    btbGrandTotal.closing =
      btbGrandTotal.opening +
      btbGrandTotal.totIn +
      btbGrandTotal.com -
      (btbGrandTotal.totOut + btbGrandTotal.exp);

    const difference = Math.abs(
      bankersGrandTotal.closing - btbGrandTotal.closing,
    );

    return {
      bankersRows,
      bankersGrandTotal,
      btbGrandTotal,
      isMatched: difference < 1,
      diff: difference,
    };
  }, [bankerTxs, branchTxs]);

  const styles = {
    tableMain: {
      width: "100%",
      borderCollapse: "collapse",
      border: "2px solid #000",
    },
    mainHeader: {
      border: "1px solid #000",
      padding: "12px",
      textAlign: "center",
      backgroundColor: "#fff",
      fontWeight: "bold",
    },
    subHeader: { backgroundColor: "#fff", fontWeight: "bold" },
    border: { border: "1px solid #000", padding: "8px" },
    cell: { border: "1px solid #000", padding: "8px" },
    cellBold: { border: "1px solid #000", padding: "8px", fontWeight: "bold" },
    cellRight: { border: "1px solid #000", padding: "8px", textAlign: "right" },
    cellCenter: {
      border: "1px solid #000",
      padding: "8px",
      textAlign: "center",
    },
  };
  console.log("Audit Data:", auditData.bankersRows); // Debugging log to verify calculations
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <h1>
          <FaExchangeAlt /> BTB vs Bankers Reconciliation (BTB Only)
        </h1>
        <div>
          <Link to="/">
            <button
              className="Branch-nav"
              style={{
                marginRight: "10px",
                padding: "8px 16px",
                cursor: "pointer",
              }}
            >
              Back To Admin
            </button>
          </Link>
          <button
            onClick={fetchData}
            disabled={loading}
            className="btn-refresh"
            style={{ padding: "8px 16px", cursor: "pointer" }}
          >
            <FaSyncAlt className={loading ? "spin" : ""} /> Sync Data
          </button>
        </div>
      </header>

      <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
        {/* BANKERS DETAILS TABLE */}
        <div style={{ flex: 3 }}>
          <table style={styles.tableMain}>
            <thead>
              <tr>
                <th colSpan="9" style={styles.mainHeader}>
                  BANKER INTERACTIONS
                </th>
              </tr>
              <tr style={styles.subHeader}>
                <th style={styles.border}>S.NO</th>
                <th style={styles.border}>BANKER</th>
                <th style={styles.border}>OPENING</th>
                <th style={styles.border}>TOT IN</th>
                <th style={styles.border}>TOT OUT</th>
                <th style={styles.border}>COM</th>
                <th style={styles.border}>EXP</th>
                <th style={styles.border}>CLOSING</th>
              </tr>
            </thead>
            <tbody>
              {auditData.bankersRows.map((row, idx) => (
                <tr key={idx}>
                  <td style={styles.cellCenter}>{idx + 1}</td>
                  <td style={styles.cell}>{row.banker}</td>{" "}
                  <td style={styles.cellRight}>
                    {row.opening.toLocaleString()}
                  </td>
                  <td style={styles.cellRight}>{row.totIn.toLocaleString()}</td>
                  <td style={styles.cellRight}>
                    {row.totOut.toLocaleString()}
                  </td>
                  <td style={styles.cellRight}>{row.com.toLocaleString()}</td>
                  <td style={styles.cellRight}>{row.exp.toLocaleString()}</td>
                  <td style={styles.cellRight}>
                    {row.closing.toLocaleString()}
                  </td>
                </tr>
              ))}
              {/* TOTAL ROW - Corrected alignment */}
              <tr style={{ fontWeight: "bold", backgroundColor: "#f9f9f9" }}>
                <td style={styles.cell} colSpan="2">
                  TOTAL
                </td>
                <td style={styles.cellRight}>
                  {auditData.bankersGrandTotal.opening.toLocaleString()}
                </td>
                <td style={styles.cellRight}>
                  {auditData.bankersGrandTotal.totIn.toLocaleString()}
                </td>
                <td style={styles.cellRight}>
                  {auditData.bankersGrandTotal.totOut.toLocaleString()}
                </td>
                <td style={styles.cellRight}>
                  {auditData.bankersGrandTotal.com.toLocaleString()}
                </td>
                <td style={styles.cellRight}>
                  {auditData.bankersGrandTotal.exp.toLocaleString()}
                </td>
                <td style={styles.cellRight}>
                  {auditData.bankersGrandTotal.closing.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* BTB DETAILS TABLE */}
        <div style={{ flex: 1 }}>
          <table style={styles.tableMain}>
            <thead>
              <tr>
                <th colSpan="2" style={styles.mainHeader}>
                  BRANCH (BTB)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.cellBold}>OPENING</td>
                <td style={styles.cellRight}>
                  {auditData.btbGrandTotal.opening.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={styles.cellBold}>TOT IN</td>
                <td style={styles.cellRight}>
                  {auditData.btbGrandTotal.totIn.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={styles.cellBold}>TOT OUT</td>
                <td style={styles.cellRight}>
                  {auditData.btbGrandTotal.totOut.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={styles.cellBold}>COM</td>
                <td style={styles.cellRight}>
                  {auditData.btbGrandTotal.com.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={styles.cellBold}>EXP</td>
                <td style={styles.cellRight}>
                  {auditData.btbGrandTotal.exp.toLocaleString()}
                </td>
              </tr>
              <tr style={{ backgroundColor: "#f0fdf4" }}>
                <td style={styles.cellBold}>CLOSING</td>
                <td
                  style={{
                    ...styles.cellRight,
                    color: "#166534",
                    fontWeight: "bold",
                  }}
                >
                  {auditData.btbGrandTotal.closing.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* MATCHING STATUS */}
      <div
        style={{
          border: "2px solid #000",
          padding: "20px",
          textAlign: "center",
          backgroundColor: auditData.isMatched ? "#f0fdf4" : "#fef2f2",
          fontWeight: "bold",
        }}
      >
        {auditData.isMatched ? (
          <div style={{ color: "#166534", fontSize: "1.3rem" }}>
            <FaCheckCircle /> MATCHING STATUS: ALL BTB CLOSING BALANCES MATCH
          </div>
        ) : (
          <div style={{ color: "#991b1b" }}>
            <div style={{ fontSize: "1.3rem" }}>
              <FaExclamationTriangle /> MATCHING STATUS: MISMATCH FOUND
            </div>
            <div style={{ marginTop: "10px" }}>
              Difference: ₹{auditData.diff.toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
